import { useCallback, useEffect, useRef, useState } from 'react';

import { dashboardApi } from '../../services/api';
import type { DashboardNotificationItem, DashboardNotificationResponse, DashboardNotificationSeverity } from '../../types';
import { useToast } from '../ui';
import { countKeys } from './notificationMeta';

export const NOTIFICATION_REFRESH_MS = 2 * 60 * 1000;
export const NOTIFICATION_STALE_MS = 30 * 1000;
// The bell and the notifications page each load their own list; a change in one tells the other to reload.
const CHANGED_EVENT = 'notifications:changed';

const EMPTY: DashboardNotificationResponse = {
    total: 0,
    counts: { homework: 0, attendance: 0, manual: 0, staff: 0 },
    items: [],
    unread: 0,
    unreadCounts: { homework: 0, attendance: 0, manual: 0, staff: 0 },
    starred: [],
};

function updateItems(data: DashboardNotificationResponse, ids: Set<string>, patch: Partial<DashboardNotificationItem>) {
    const apply = (item: DashboardNotificationItem) => (ids.has(item.id) ? { ...item, ...patch } : item);
    return { ...data, items: data.items.map(apply), starred: (data.starred ?? []).map(apply) };
}

// Unread totals follow the items after a local change, until the next load brings the server's numbers.
function recountUnread(previous: DashboardNotificationResponse, next: DashboardNotificationResponse) {
    const unreadCounts = { ...(previous.unreadCounts ?? EMPTY.unreadCounts!) };
    const before = new Map(previous.items.map(item => [item.id, item]));
    for (const item of next.items) {
        const old = before.get(item.id);
        if (!old || Boolean(old.read) === Boolean(item.read)) continue;
        const key = countKeys[item.source];
        unreadCounts[key] = Math.max(0, (unreadCounts[key] ?? 0) + (item.read ? -1 : 1));
    }
    const unread = Object.values(unreadCounts).reduce((sum, value) => sum + (value ?? 0), 0);
    return { ...next, unreadCounts, unread };
}

// Lets the bell and the notifications page know a notification changed somewhere else (e.g. the payroll page).
export function announceNotificationsChanged() {
    window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: null }));
}

export function useNotifications({ full = false, poll = false } = {}) {
    const { showToast } = useToast();
    const [data, setData] = useState<DashboardNotificationResponse>(EMPTY);
    const [loading, setLoading] = useState(true);
    const mountedRef = useRef(true);
    const lastLoadedRef = useRef(0);
    // Tells this list's own announcements apart from the other one's.
    const instanceRef = useRef({});

    const load = useCallback(async (background = false) => {
        if (!background) setLoading(true);
        try {
            lastLoadedRef.current = Date.now();
            const response = await dashboardApi.getNotifications(full ? { full: true } : {});
            if (mountedRef.current) setData({ ...EMPTY, ...response.data.data });
        } catch (error: any) {
            if (mountedRef.current && !background) showToast(error?.message || 'Không tải được thông báo.', 'error');
        } finally {
            if (mountedRef.current && !background) setLoading(false);
        }
    }, [full, showToast]);

    const refreshIfStale = useCallback(() => {
        if (Date.now() - lastLoadedRef.current > NOTIFICATION_STALE_MS) load(true);
    }, [load]);

    useEffect(() => {
        mountedRef.current = true;
        load();

        const reload = (event: Event) => {
            if ((event as CustomEvent).detail !== instanceRef.current) load(true);
        };
        window.addEventListener(CHANGED_EVENT, reload);
        let timer: number | undefined;
        const refreshOnReturn = () => {
            // Switching tabs back and forth fires this constantly; only refetch once the list is stale.
            if (document.visibilityState === 'visible') refreshIfStale();
        };
        if (poll) {
            timer = window.setInterval(() => {
                if (document.visibilityState === 'visible') load(true);
            }, NOTIFICATION_REFRESH_MS);
            document.addEventListener('visibilitychange', refreshOnReturn);
        }

        return () => {
            mountedRef.current = false;
            window.removeEventListener(CHANGED_EVENT, reload);
            if (timer) window.clearInterval(timer);
            document.removeEventListener('visibilitychange', refreshOnReturn);
        };
    }, [load, poll, refreshIfStale]);

    const announce = () => window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: instanceRef.current }));

    const markRead = useCallback(async (items: DashboardNotificationItem[], read = true) => {
        const targets = items.filter(item => Boolean(item.read) !== read);
        if (!targets.length) return;
        const ids = new Set(targets.map(item => item.id));
        setData(current => recountUnread(current, updateItems(current, ids, { read })));
        try {
            await dashboardApi.markNotificationsRead([...ids], read);
            announce();
        } catch (error: any) {
            showToast(error?.message || 'Không đánh dấu được thông báo.', 'error');
            load(true);
        }
    }, [load, showToast]);

    const toggleStar = useCallback(async (item: DashboardNotificationItem) => {
        const starred = !item.starred;
        setData(current => {
            const next = updateItems(current, new Set([item.id]), { starred });
            const others = (next.starred ?? []).filter(entry => entry.id !== item.id);
            return { ...next, starred: starred ? [{ ...item, starred }, ...others] : others };
        });
        try {
            await dashboardApi.starNotification(item.id, starred);
            announce();
        } catch (error: any) {
            showToast(error?.message || 'Không gắn sao được.', 'error');
            load(true);
        }
    }, [load, showToast]);

    const setPriority = useCallback(async (item: DashboardNotificationItem, severity: DashboardNotificationSeverity) => {
        setData(current => updateItems(current, new Set([item.id]), { severity }));
        try {
            const isDefault = item.source !== 'MANUAL' && severity === (item.defaultSeverity ?? item.severity);
            await dashboardApi.setNotificationPriority(item.id, isDefault ? null : severity);
            showToast('Đã đổi mức độ.', 'success');
            announce();
        } catch (error: any) {
            showToast(error?.message || 'Không đổi được mức độ.', 'error');
            load(true);
        }
    }, [load, showToast]);

    return { data, loading, load, refreshIfStale, markRead, toggleStar, setPriority };
}
